import { FC, useRef, useEffect } from 'react';

import { Box, Divider, Typography } from '@mui/material';

import { useFormContext } from 'react-hook-form';

import { useMount } from 'react-use';

import { useStores } from 'mobx/hooks/useStores';

import { getFormattedPhoneNumber } from 'utils/PhoneUtils';

import { RequestConsentMessageStatus } from 'models/Patient';

import Icon from 'components/Icons/Icon';
import { usePatientModel } from 'components/Patient/usePatientModel';

import { useConsentVerification } from 'components/Ticket/TicketForms/useConsentVerification';
import { OutlinedIconButton, TextButton } from 'components/UIkit/atoms/Button';
import { Text } from 'components/UIkit/atoms/Text';

const PATIENT_POLLING_INTERVAL_FOR_STATUS = 10000; // 10 seconds

interface VerificationStatusPanelProps {
  onChangeContact: () => void;
  shouldAutoSendOnMount?: boolean;
}

export const VerificationStatusPanel: FC<VerificationStatusPanelProps> = ({
  onChangeContact,
  shouldAutoSendOnMount = false
}) => {
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const { patientPageStore } = useStores();
  const patient = usePatientModel();

  const {
    recipientName,
    isMessageSending,
    resendDisabled,
    handleSendConsentRequest,
    isAlreadyConsented,
    isConsentDeclined,
    getVerificationDate,
    isConsentVerificationEnabled,
    verificationResponseReceived,
    resetVerificationFlow
  } = useConsentVerification();

  const { watch } = useFormContext();
  const { countryCode, phone } = watch();

  const phoneDisplay = getFormattedPhoneNumber(countryCode + phone);

  const shouldShowVerificationDisclaimer = !verificationResponseReceived;

  // Reset verification state and conditionally auto-send on panel mount
  useMount(function resetAndSendConsentVerificationOnMount() {
    resetVerificationFlow();
    // Only auto-send if this is a fresh verification flow (user explicitly selected "Yes")
    if (shouldAutoSendOnMount && !isMessageSending) {
      handleSendConsentRequest();
    }
  });

  // Setup consent status polling
  useEffect(
    function setupConsentStatusPolling() {
      const shouldPoll =
        isConsentVerificationEnabled &&
        patient?.id &&
        [RequestConsentMessageStatus.Pending, RequestConsentMessageStatus.Sent].includes(
          patient?.requestConsentMessageStatus
        );

      if (shouldPoll && !pollIntervalRef.current) {
        pollIntervalRef.current = setInterval(async () => {
          await patientPageStore.updateConsentStatusOnly(patient.id);
        }, PATIENT_POLLING_INTERVAL_FOR_STATUS);
      } else if (!shouldPoll && pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }

      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      };
    },
    [
      isConsentVerificationEnabled,
      patient?.id,
      patient?.requestConsentMessageStatus,
      patientPageStore
    ]
  );

  const handleChangeContactClick = () => {
    resetVerificationFlow();
    onChangeContact();
  };

  const renderVerificationStatus = () => {
    // Always show pending state until we get a new response
    if (!verificationResponseReceived) {
      return (
        <>
          <Text variant="body1" color="primary" fontWeight="medium">
            Request Sent — Awaiting Verification
          </Text>
          <Text variant="body2" color="primary">
            Waiting for {recipientName} to verify consent by replying 'yes' to the text message.
          </Text>
          <Box display="flex" alignItems="center" gap={8}>
            <TextButton
              variant="secondary"
              onClick={handleSendConsentRequest}
              disabled={resendDisabled || isMessageSending}
            >
              {isMessageSending ? 'Sending...' : 'Resend SMS'}
            </TextButton>
          </Box>
        </>
      );
    }

    if (verificationResponseReceived) {
      if (isAlreadyConsented) {
        return (
          <Box display="flex" alignItems="center" gap={4}>
            <Icon.SuccessCheckmark />
            <Typography variant="body1" color="primary.dark">
              Consent Verified via SMS on {getVerificationDate()}
            </Typography>
          </Box>
        );
      }

      if (isConsentDeclined) {
        return (
          <Box display="flex" alignItems="center" gap={4}>
            <Icon.ErrorXRed />
            <Text variant="body1" color="error">
              Consent Declined via SMS on {getVerificationDate()}
            </Text>
          </Box>
        );
      }
    }
  };

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={16}>
        <OutlinedIconButton
          onClick={handleChangeContactClick}
          icon={<Icon.Chevron left />}
          testHook="change-contact"
          variant="secondary"
        >
          Change Contact
        </OutlinedIconButton>
        <Box textAlign="right" flex={1} minWidth={0}>
          <Text variant="h4" component="div" isEllipsis>
            {recipientName}
          </Text>
          <Text variant="form-text" color="secondary">
            {phoneDisplay}
          </Text>
        </Box>
      </Box>

      <Divider sx={{ my: 20 }} />

      <Box>
        <Box display="flex" flexDirection="column" gap={4}>
          {renderVerificationStatus()}
        </Box>
      </Box>

      {shouldShowVerificationDisclaimer && (
        <>
          <Divider sx={{ my: 20 }} />

          <Box alignItems="center" display="flex" gap={8}>
            <Text variant="body2" color="secondary" component="div" fontStyle="italic">
              <Icon.Info width={18} height={18} />
            </Text>
            <Text variant="body2" color="secondary" component="div" fontStyle="italic">
              You may leave this page or proceed with ticket creation before consent is verified.
            </Text>
          </Box>
        </>
      )}
    </>
  );
};
